// ============================================================================
// DESTINATION:  src/core/utils/session-broadcast.ts
// ACTION:       NEW FILE
// ============================================================================

/**
 * ============================================================================
 * FILE: src/core/utils/session-broadcast.ts
 * ============================================================================
 * Cross-tab session signalling. When one tab logs in, logs out, or is force-
 * logged-out by the server, every OTHER tab in the same browser needs to know
 * within milliseconds — not on its next focus, which may be never while the user
 * is in a different tab.
 *
 * ============================================================================
 * TWO CHANNELS ON PURPOSE
 * ============================================================================
 *   BroadcastChannel  — instant, structured, but not universal (older Safari).
 *   localStorage      — the `storage` event fires in OTHER tabs when a key
 *                       changes. Universally supported. The fallback AND the
 *                       belt-and-braces.
 *
 * We publish on BOTH and dedupe on receive. If BroadcastChannel is missing, the
 * storage event still carries it. If both fire, the id dedupe drops the second.
 *
 * ⚠️ THE storage EVENT DOES NOT FIRE IN THE TAB THAT WROTE IT. So publish() also
 * calls local subscribers directly — otherwise the tab that logged out would be
 * the one tab that never hears about it.
 *
 * NOTE: this file uses localStorage deliberately. It is the cross-tab transport;
 * there is no other browser API that notifies other tabs. It is NOT session
 * storage of credentials — it carries a tiny event `{ type, id, at }` and nothing
 * sensitive.
 */

export type SessionEventType =
  | "LOGGED_IN"
  | "LOGGED_OUT"
  | "SESSION_INVALIDATED";

export interface SessionEvent {
  type: SessionEventType;
  /** Random per-emit id, so a tab can ignore its own echo and dedupe channels. */
  id: string;
  at: number;
}

type Handler = (event: SessionEvent) => void;

const STORAGE_KEY = "sob.session.event";
const CHANNEL_NAME = "sob.session";

const handlers = new Set<Handler>();
const seenIds = new Set<string>();

let channel: BroadcastChannel | null = null;
if (typeof BroadcastChannel !== "undefined") {
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (e: MessageEvent<SessionEvent>) => deliver(e.data);
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      deliver(JSON.parse(e.newValue) as SessionEvent);
    } catch {
      /* a malformed value from another origin/tab — ignore */
    }
  });
}

function deliver(event: SessionEvent): void {
  if (!event || !event.id) return;
  // Dedupe: BroadcastChannel + storage may both deliver the same emit.
  if (seenIds.has(event.id)) return;
  seenIds.add(event.id);
  // Keep the set from growing forever.
  if (seenIds.size > 50) seenIds.clear();

  handlers.forEach((h) => h(event));
}

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Emit an event to every OTHER tab, and to local subscribers in THIS tab.
 */
export function publishSessionEvent(type: SessionEventType): void {
  const event: SessionEvent = { type, id: makeId(), at: Date.now() };

  // Mark our own id seen BEFORE we emit, so the storage/channel echo is ignored.
  seenIds.add(event.id);

  // Other tabs, via both transports.
  channel?.postMessage(event);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
  } catch {
    /* private mode / quota — BroadcastChannel still carried it */
  }

  // THIS tab — the storage event will not fire here, so notify directly.
  handlers.forEach((h) => h(event));
}

/** Convenience wrappers so call sites read clearly. */
export const broadcastLoggedIn = () => publishSessionEvent("LOGGED_IN");
export const broadcastLoggedOut = () => publishSessionEvent("LOGGED_OUT");
export const broadcastSessionInvalidated = () =>
  publishSessionEvent("SESSION_INVALIDATED");

/**
 * Subscribe. Returns an unsubscribe function. SessionWatcher is the single
 * subscriber in the app.
 */
export function onSessionEvent(handler: Handler): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}
